/**
 * How much of the cartoon plays for free: the experiment.
 *
 * `teaser` (control) stops after the first scene and asks to be unlocked,
 * which opens the paywall. `full` plays the whole cartoon. Mixpanel assigns
 * the variant through the feature flag `cartoon_gate` and records the
 * exposure the moment it is read — which is here, when the parent lands on
 * the catalogue, so only parents who actually saw a billboard are in the
 * experiment. Both funnels use this, and every event they send carries the
 * variant as `cartoon_gate`, so results can be split by `funnel`.
 *
 * Until Mixpanel answers (or if it never does) the control plays: a visitor
 * on the fallback saw the teaser and is not counted as exposed.
 */
import { useEffect, useState } from 'react';
import { analytics } from '../analytics';

export type CartoonGate = 'teaser' | 'full';

/** The Mixpanel feature-flag key. Variant values are the gate names. */
export const CARTOON_GATE_FLAG = 'cartoon_gate';

/** Scenes that play before the gate in the control. */
const TEASER_SCENES = 1;

export const freeScenesFor = (gate: CartoonGate): number | undefined =>
  gate === 'full' ? undefined : TEASER_SCENES;

/** Where a tester's choice of arm is kept on this device (see {@link setForcedGate}). */
const FORCED_KEY = 'ct.cartoonGate';

/**
 * A forced arm: `?full` / `?teaser` in development, or the arm a tester picked
 * on the device (five taps on the avatar at the reveal). A forced arm is
 * recorded as such on every event, so QA sessions can be excluded from the
 * experiment's results.
 */
export function forcedGate(): CartoonGate | null {
  if (import.meta.env.DEV) {
    const q = new URLSearchParams(location.search);
    if (q.has('full')) return 'full';
    if (q.has('teaser')) return 'teaser';
  }
  try {
    const v = localStorage.getItem(FORCED_KEY);
    if (v === 'full' || v === 'teaser') return v;
  } catch { /* private mode */ }
  return null;
}
const forced = forcedGate;

/** Pin this device to one arm (null = back to Mixpanel's assignment). Takes effect on the next catalogue. */
export function setForcedGate(gate: CartoonGate | null): void {
  try {
    if (gate) localStorage.setItem(FORCED_KEY, gate);
    else localStorage.removeItem(FORCED_KEY);
  } catch { /* private mode */ }
  resolved = null; // ask again next time
  analytics.register({ cartoon_gate: gate ?? 'unassigned', gate_forced: !!gate });
}

let resolved: Promise<CartoonGate> | null = null;

/** Ask once per page; every later ask shares the answer. */
export function cartoonGate(): Promise<CartoonGate> {
  const f = forced();
  if (f) {
    analytics.register({ cartoon_gate: f, gate_forced: true });
    return Promise.resolve(f);
  }
  if (!resolved) {
    resolved = analytics.variant(CARTOON_GATE_FLAG, 'teaser').then((v) => {
      // Mixpanel's default variant keys are control / treatment; the flag may
      // carry either naming, so both spellings of "the whole cartoon" count.
      const gate: CartoonGate = v.value === 'full' || v.value === 'treatment' ? 'full' : 'teaser';
      // Every later event says which arm this visitor saw — or that they were
      // never assigned one, which is not the same thing as the control.
      analytics.register({ cartoon_gate: v.assigned ? gate : 'unassigned' });
      return gate;
    });
  }
  return resolved;
}

/** The gate for this visitor; the control until Mixpanel answers. */
export function useCartoonGate(): CartoonGate {
  const [gate, setGate] = useState<CartoonGate>(() => forced() ?? 'teaser');
  useEffect(() => {
    let alive = true;
    void cartoonGate().then((g) => { if (alive) setGate(g); });
    return () => { alive = false; };
  }, []);
  return gate;
}
